import React from 'react';
import { Check, CreditCard, Tag, Loader2, ArrowUpCircle, PlusCircle, Calendar, CalendarDays, Car, ArrowRight, Gift } from 'lucide-react';
import { PLAN_OPTIONS, PlanType } from '../../../services/tipos';

/** Simulação da expansão como o servidor vai cobrar: acréscimo mensal de tabela × dias restantes ÷ 30. */
export interface ExpansaoSimulacao {
  ehLocaSmartTrack: boolean;
  valorProRata: number;
  valorPorPlacaMensal: number;
  atualValorMensal: number;
  novoValorMensal: number;
  diasRestantes: number;
  limiteAtual: number;
  blocosAdicionais: number;
  valorBloco: number;
  acrescimoMensalPlano: number;
  placasAdicionais: number;
  acrescimoMensalCheck: number;
  acrescimoMensal: number;
  motivo: string | null;
}

interface CheckoutSummaryProps {
  vehicleCount: number;
  currentVehicleLimit: number;
  basePrice: number;
  totalPrice: number;
  discount: number;
  finalPrice: number;
  onCheckout: () => void;
  paymentMethod: 'pix' | 'creditcard';
  selectedPlan: PlanType;
  couponCode: string;
  setCouponCode: (code: string) => void;
  onCouponApply: (code: string) => void;
  isButtonsDisabled: boolean;
  countdown: number;
  temPlanoAtivo: boolean;
  diasRestantes: number;
  precoAtual: number;
  precoNovo: number;
  precoMensalSemDesconto: number;
  hasActivePayment: boolean;
  tipoPlano: 'PRO' | 'AVANCADO';
  acao: 'upgrade' | 'expandir' | null;
  currentPlanType?: string;
  incluirLocaSmartCheck?: boolean;
  precoLocaSmartCheck?: number;
  onRemoveLocaSmartCheck?: () => void;
  descontoPercentual?: number;
  precoLocaSmartCheckMensal?: number;
  precoLocaSmartCheckMensalAtual?: number;
  indicadorNome?: string;
  ocultarBotaoAcao?: boolean;
  valorAtualMensalServidor?: number;
  valorNovoMensalServidor?: number;
  expansao?: ExpansaoSimulacao | null;
}

const CheckoutSummary: React.FC<CheckoutSummaryProps> = ({
  vehicleCount,
  currentVehicleLimit,
  basePrice,
  totalPrice,
  discount,
  finalPrice,
  onCheckout,
  paymentMethod,
  selectedPlan,
  couponCode,
  setCouponCode,
  onCouponApply,
  isButtonsDisabled,
  countdown,
  temPlanoAtivo,
  diasRestantes,
  precoAtual,
  precoNovo,
  precoMensalSemDesconto,
  hasActivePayment,
  tipoPlano,
  acao,
  currentPlanType,
  incluirLocaSmartCheck = false,
  precoLocaSmartCheck = 0,
  onRemoveLocaSmartCheck,
  descontoPercentual,
  precoLocaSmartCheckMensal = 0,
  precoLocaSmartCheckMensalAtual = 0,
  indicadorNome,
  ocultarBotaoAcao = false,
  valorAtualMensalServidor,
  valorNovoMensalServidor,
  expansao = null,
}) => {
  const selectedPlanOption = PLAN_OPTIONS.find(plan => plan.id === selectedPlan);

  const formatCurrency = (value: number) => {
    return value.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    });
  };

  const getTitle = () => {
    if (temPlanoAtivo) {
      if (acao === 'upgrade') return "Upgrade de Plano";
      if (acao === 'expandir') return "Expandir Frota";
    }
    return "Resumo da Assinatura";
  }

  const ehUpgrade = acao === 'upgrade';
  const ehExpansao = acao === 'expandir';
  const estaAumentando = ehUpgrade || ehExpansao;
  // Expansão sem valor cobrável (simulação pendente, quantidade igual, plano sem vigência) não gera cobrança.
  const expansaoSemValor = ehExpansao && (!expansao || !!expansao.motivo || expansao.valorProRata <= 0);

  // Valores do servidor: o "atual" é o que o cliente paga hoje no Asaas (pode ser a tabela antiga) e já inclui o
  // LocaSmartCheck, a mesma base do "Valor a pagar AGORA". Sem eles, a tela usa a tabela local.
  const usaValoresDoServidor = !!valorAtualMensalServidor && !!valorNovoMensalServidor;
  const precoAtualExibido = usaValoresDoServidor ? valorAtualMensalServidor! : precoAtual;
  const precoNovoExibido = usaValoresDoServidor ? valorNovoMensalServidor! : precoNovo;
  const mostrarCheckSeparado = incluirLocaSmartCheck && !usaValoresDoServidor;

  const periodoTexto = selectedPlan === 'MONTHLY' ? '/mês' :
    selectedPlan === 'QUARTERLY' ? '/trimestre' :
      selectedPlan === 'SEMIANNUAL' ? '/semestre' : '/ano';

  const economiaTotalCiclo = selectedPlanOption
    ? (precoMensalSemDesconto * selectedPlanOption.cycleMonths) - (precoNovo * selectedPlanOption.cycleMonths)
    : 0;

  // Tratamento visual do nome do plano atual
  const displayCurrentPlan = currentPlanType === 'AVANCADO' ? 'Plano Avançado' : 'Plano Pro';
  const displayTargetPlan = tipoPlano === 'AVANCADO' ? 'Plano Avançado' : 'Plano Pro';

  return (
    <div className="rounded-xl border shadow-xl sticky top-6 bg-white border-blue-100 overflow-hidden">
      {/* Header com a cor da marca */}
      <div className="bg-[#1F467E] p-4 text-white">
        <h2 className="text-lg font-bold flex items-center gap-2 text-white">
          {ehUpgrade && <ArrowUpCircle className="w-6 h-6 text-green-400" />}
          {ehExpansao && <PlusCircle className="w-6 h-6 text-blue-300" />}
          {!estaAumentando && <Tag className="w-5 h-5" />}
          {getTitle()}
        </h2>
      </div>

      <div className="p-4">
        {hasActivePayment && (
          <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6 rounded-r-md animate-pulse">
            <div className="flex items-center">
              <Loader2 className="h-5 w-5 text-blue-400 animate-spin mr-3" />
              <div>
                <p className="text-sm font-medium text-blue-700">Pagamento em processamento</p>
                <p className="text-xs text-blue-600">Aguarde a confirmação automática.</p>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-3">
          {temPlanoAtivo && estaAumentando ? (
            /* Layout Exclusivo para Upgrade/Expansão */
            <div className="space-y-4">

              {/* Card de Comparação da Situação */}
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <div className="flex justify-between items-center mb-3">
                  <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">Situação Atual</span>
                  <span className="text-xs font-bold text-[#1F467E] uppercase tracking-wide">Nova Situação</span>
                </div>

                <div className="flex justify-between items-center relative">
                  {/* Lado Esquerdo - Atual */}
                  <div>
                    <p className="text-sm font-bold text-gray-700 mb-0.5">{displayCurrentPlan}</p>
                    <p className="text-sm font-medium text-gray-600">{currentVehicleLimit} Veículos</p>
                    <p className="text-xs text-gray-500 mt-1">{formatCurrency(precoAtualExibido)}/mês</p>
                    {incluirLocaSmartCheck && usaValoresDoServidor && <p className="text-xs text-gray-400 mt-0.5">inclui LocaSmartCheck</p>}
                    {mostrarCheckSeparado && precoLocaSmartCheckMensalAtual > 0 && (
                      <p className="text-xs text-gray-400 mt-0.5">+ LocaSmartCheck {formatCurrency(precoLocaSmartCheckMensalAtual)}/mês</p>
                    )}
                  </div>

                  {/* Seta Central */}
                  <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white p-1.5 rounded-full shadow-sm border border-blue-100 z-10">
                    <ArrowRight size={18} className="text-[#1F467E]" />
                  </div>

                  {/* Lado Direito - Novo */}
                  <div className="text-right">
                    <p className="text-sm font-bold text-[#1F467E] mb-0.5">{displayTargetPlan}</p>
                    <p className="text-sm font-bold text-[#1F467E]">{vehicleCount} Veículos</p>
                    <p className="text-xs font-bold text-[#1F467E] mt-1">{formatCurrency(precoNovoExibido)}/mês</p>
                    {incluirLocaSmartCheck && usaValoresDoServidor && <p className="text-xs text-[#1F467E] mt-0.5">inclui LocaSmartCheck</p>}
                    {mostrarCheckSeparado && precoLocaSmartCheckMensal > 0 && (
                      <p className="text-xs text-[#1F467E] mt-0.5">+ LocaSmartCheck {formatCurrency(precoLocaSmartCheckMensal)}/mês</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Detalhes do Pagamento Proporcional */}
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                <h3 className="text-sm font-bold text-[#1F467E] mb-3 flex items-center gap-2">
                  <Calendar size={16} />
                  Resumo do Pagamento
                </h3>

                <div className="space-y-3 text-sm">
                  {ehExpansao && expansao && !expansao.ehLocaSmartTrack ? (
                    <>
                      <div className="flex justify-between items-center text-xs text-gray-500">
                        <span>Blocos de 5 placas</span>
                        <span>{expansao.blocosAdicionais} × {formatCurrency(expansao.valorBloco)} = {formatCurrency(expansao.acrescimoMensalPlano)}/mês</span>
                      </div>
                      {expansao.acrescimoMensalCheck > 0 && (
                        <div className="flex justify-between items-center text-xs text-gray-500">
                          <span>LocaSmartCheck</span>
                          <span>{expansao.placasAdicionais} placas = {formatCurrency(expansao.acrescimoMensalCheck)}/mês</span>
                        </div>
                      )}
                      <div className="flex justify-between items-center text-xs text-gray-500">
                        <span>Proporcional ao período pago</span>
                        <span>{formatCurrency(expansao.acrescimoMensal)} ÷ 30 × {expansao.diasRestantes} dias</span>
                      </div>
                      {expansao.motivo && (
                        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">{expansao.motivo}</p>
                      )}
                    </>
                  ) : ehExpansao && !expansao ? (
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <Loader2 className="h-4 w-4 animate-spin" /> Calculando o valor da expansão...
                    </div>
                  ) : (
                    <>
                      <div className="flex justify-between items-center text-xs text-gray-500">
                        <span>Plano proporcional</span>
                        <span>({formatCurrency(precoNovoExibido)} - {formatCurrency(precoAtualExibido)}) ÷ 30 × {diasRestantes} dias</span>
                      </div>

                      {mostrarCheckSeparado && precoLocaSmartCheckMensal > 0 && (
                        <div className="flex justify-between items-center text-xs text-gray-500">
                          <span>LocaSmartCheck proporcional</span>
                          <span>({formatCurrency(precoLocaSmartCheckMensal)} - {formatCurrency(precoLocaSmartCheckMensalAtual)}) ÷ 30 × {diasRestantes} dias</span>
                        </div>
                      )}
                    </>
                  )}

                  <div className="pt-3 mt-2 border-t border-blue-200">
                    <div className="flex justify-between items-end">
                      <span className="text-[#1F467E] font-bold">Valor a pagar AGORA:</span>
                      <span className="text-2xl font-bold text-[#1F467E]">{formatCurrency(finalPrice)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* Layout Padrão de Assinatura */
            <div className="bg-white p-2 rounded-md">
              <div className="flex justify-between items-center mb-2 pb-2 border-b border-gray-100">
                <span className="text-gray-600">Plano Escolhido</span>
                <span className="font-bold text-[#1F467E] text-lg">{tipoPlano === 'AVANCADO' ? 'Avançado' : 'Pro'}</span>
              </div>

              <div className="space-y-2 text-sm text-gray-600">
                <div className="flex justify-between">
                  <span>Limite de Veículos</span>
                  <span className="font-medium text-black">{vehicleCount} Placas</span>
                </div>

                <div className="flex justify-between">
                  <span>Preço Base</span>
                  <span>{formatCurrency(basePrice)}</span>
                </div>

                {vehicleCount > (tipoPlano === 'AVANCADO' ? 10 : 5) && (
                  <div className="flex justify-between text-gray-500 text-xs">
                    <span>Adicional por veículo</span>
                    <span>+ {formatCurrency(precoMensalSemDesconto - basePrice)}</span>
                  </div>
                )}

                {selectedPlanOption && selectedPlanOption.cycleMonths > 1 && (
                  <div className="bg-green-50 p-2 rounded border border-green-100 mt-2">
                    <div className="flex justify-between text-green-700 font-medium items-center">
                      <span>Desconto aplicado ({((descontoPercentual ?? selectedPlanOption.discount) * 100).toFixed(0)}%)</span>
                      <span>-{formatCurrency(economiaTotalCiclo)}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* LocaSmartCheck Addon */}
              {incluirLocaSmartCheck && precoLocaSmartCheck > 0 && (
                <div className="mt-2 pt-2 border-t border-gray-100">
                  <div className="flex justify-between items-center">
                    <div>
                      <span className="text-sm font-medium text-[#132f4c]">LocaSmartCheck</span>
                      <span className="text-xs text-gray-400 ml-1">({vehicleCount} placas)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-[#132f4c]">
                        +{formatCurrency(precoLocaSmartCheck)}
                      </span>
                      {onRemoveLocaSmartCheck && (
                        <button
                          onClick={onRemoveLocaSmartCheck}
                          className="text-[11px] text-red-400 hover:text-red-600 cursor-pointer border-none bg-transparent underline underline-offset-2 transition-colors"
                        >
                          Remover
                        </button>
                      )}
                    </div>
                  </div>
                  <p className="text-[10px] text-gray-400 mt-0.5">Vistorias profissionais — com o desconto do plano</p>
                </div>
              )}

              {/* Área de Cupom */}
              {!temPlanoAtivo && selectedPlanOption && selectedPlanOption.cycleMonths === 1 && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <div className="flex items-center space-x-2 mb-2">
                    <Tag className="h-3 w-3 text-[#1F467E]" />
                    <span className="text-xs font-semibold text-[#1F467E] uppercase tracking-wide">Cupom de Desconto</span>
                  </div>
                  <div className="flex space-x-2">
                    <input
                      id="coupon"
                      type="text"
                      placeholder="Código"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value)}
                      className="flex-1 h-9 px-3 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-[#1F467E] focus:border-[#1F467E] outline-none"
                      disabled={isButtonsDisabled || hasActivePayment || selectedPlan !== 'MONTHLY'}
                    />
                    <button
                      onClick={() => onCouponApply(couponCode)}
                      disabled={isButtonsDisabled || hasActivePayment || selectedPlan !== 'MONTHLY'}
                      className="px-4 h-9 text-sm bg-gray-100 text-gray-700 font-medium hover:bg-gray-200 rounded transition-colors disabled:opacity-50"
                    >
                      Aplicar
                    </button>
                  </div>
                  {discount > 0 && !indicadorNome && (
                    <div className="flex justify-between mt-2 text-green-600 text-sm font-medium">
                      <span>Desconto obtido</span>
                      <span>-{formatCurrency(discount)}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Atribuição de afiliação — quem indicou (compacto) */}
              {indicadorNome && !temPlanoAtivo && (
                <div className="mt-2">
                  <div className="flex justify-between items-center text-sm font-medium text-green-600">
                    <span className="flex items-center gap-1">
                      <Gift className="h-3.5 w-3.5 flex-shrink-0" />
                      Desconto afiliação
                    </span>
                    {discount > 0 && <span>-{formatCurrency(discount)}</span>}
                  </div>
                  <p className="text-[11px] text-gray-400 leading-tight mt-0.5">via {indicadorNome}</p>
                </div>
              )}

              <div className="mt-3 pt-3 border-t border-gray-200">
                {incluirLocaSmartCheck && precoLocaSmartCheck > 0 && (
                  <div className="space-y-1 mb-2 text-sm text-gray-600">
                    <div className="flex justify-between">
                      <span>Plano {tipoPlano === 'AVANCADO' ? 'Avançado' : 'Pro'}</span>
                      <span>{formatCurrency(finalPrice - precoLocaSmartCheck)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>LocaSmartCheck</span>
                      <span>{formatCurrency(precoLocaSmartCheck)}</span>
                    </div>
                  </div>
                )}
                <div className="flex flex-col items-end">
                  <span className="text-sm text-gray-500">Total a Pagar</span>
                  <span className="text-3xl font-bold text-[#1F467E]">
                    {formatCurrency(finalPrice)}
                    <span className="text-sm font-normal text-gray-500 ml-1">{!temPlanoAtivo ? periodoTexto : ''}</span>
                  </span>
                  {!temPlanoAtivo && selectedPlanOption && selectedPlanOption.cycleMonths > 1 && (
                    <div className="flex flex-col items-end gap-1.5 mt-2">
                      <span className="text-[11px] font-medium uppercase tracking-wide text-gray-400">Equivalente a</span>
                      <div className="flex flex-wrap justify-end gap-1.5">
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-2.5 py-1 text-sm font-semibold text-[#1F467E]">
                          <CalendarDays className="h-3.5 w-3.5 flex-shrink-0" />
                          {formatCurrency(finalPrice / selectedPlanOption.cycleMonths)}/mês
                        </span>
                        {vehicleCount > 0 && (
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-2.5 py-1 text-sm font-semibold text-[#1F467E]">
                            <Car className="h-3.5 w-3.5 flex-shrink-0" />
                            {formatCurrency(finalPrice / selectedPlanOption.cycleMonths / vehicleCount)}/mês por placa
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Botão de Ação */}
          {!ocultarBotaoAcao && (
          <div className="mt-6">
            {hasActivePayment ? (
              <div className="w-full py-3 px-4 bg-gray-100 text-gray-500 rounded-lg text-center font-medium cursor-wait">
                Processando pagamento...
              </div>
            ) : (
              <>
                <button
                  onClick={onCheckout}
                  disabled={!(finalPrice > 0 && finalPrice !== null) && !ehUpgrade && !ehExpansao ? true : isButtonsDisabled || hasActivePayment || expansaoSemValor}
                  className={`w-full py-3.5 px-4 text-white rounded-lg transition-all shadow-md text-base font-bold flex justify-center items-center gap-2
                    ${isButtonsDisabled || hasActivePayment || expansaoSemValor
                      ? "bg-gray-400 cursor-not-allowed opacity-60"
                      : "bg-[#1F467E] hover:bg-[#162f56] hover:shadow-lg transform active:scale-95"}
                  `}
                >
                  {paymentMethod === "pix" ? (
                    <>Gerar PIX <Check size={18} /></>
                  ) : (
                    <>Confirmar Pagamento <CreditCard size={18} /></>
                  )}
                </button>

                {isButtonsDisabled && paymentMethod === "pix" && (
                  <p className="text-xs text-center text-gray-500 mt-2">
                    Aguarde {countdown}s para gerar novo código
                  </p>
                )}
              </>
            )}

            <div className="flex items-center justify-center mt-4 text-gray-400 text-xs gap-1">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              Ambiente Seguro e Criptografado
            </div>
          </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CheckoutSummary;